import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, deleteDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { updateCandidate, softDelete } from '../services/firestoreService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { hasPermission, canAccessDistrict, isStaffRole, PERMISSIONS } from '../utils/rolePermissions';
import {
  getConferenceStatusLabel,
  getConferenceStatusStyle,
  getProcessStageLabel,
  getProcessStageStyle,
} from '../utils/labelHelpers';
import CourseOfStudyTracker from '../components/CourseOfStudyTracker';
import TranscriptManager from '../components/TranscriptManager';
import ActionReportsList from '../components/ActionReportsList';
import DocumentManager from '../components/DocumentManager';
import CandidateSubmissionPortal from '../components/CandidateSubmissionPortal';
import { SkeletonProfile } from '../components/ui/Skeleton';
import Breadcrumb from '../components/ui/Breadcrumb';
import '../styles/CandidateProfile.css';

const CandidateProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [statusConfig, setStatusConfig] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [selectedSubmissionYear, setSelectedSubmissionYear] = useState(new Date().getFullYear());

  useEffect(() => {
    fetchStatusConfig();
    fetchDistricts();
  }, [id]);

  useEffect(() => {
    if (districts.length > 0) {
      fetchCandidate();
    }
  }, [id, districts]);

  const fetchCandidate = async () => {
    // Candidates can only view their own profile — short-circuit before
    // making any Firestore read if the IDs don't match.
    if (currentUser && !isStaffRole(currentUser.role) && currentUser.uid !== id) {
      navigate('/candidates');
      return;
    }

    try {
      const candidateDoc = await getDoc(doc(db, 'candidates', id));
      if (candidateDoc.exists()) {
        const candidateData = { id: candidateDoc.id, ...candidateDoc.data() };

        // Secondary check: district-scoped staff cannot access other districts
        if (!canUserAccessCandidate(candidateData)) {
          console.error('Access denied to this candidate');
          navigate('/candidates');
          return;
        }
        
        setCandidate(candidateData);
        setEditData(candidateData);
      } else {
        console.error('Candidate not found');
        navigate('/candidates');
      }
    } catch (error) {
      console.error('Error fetching candidate:', error);
    } finally {
      setLoading(false);
    }
  };

  // Check if current user can access this candidate based on district permissions
  const canUserAccessCandidate = (candidateData) => {
    if (!currentUser) return false;
    
    // Admins can access all candidates
    if (hasPermission(currentUser, PERMISSIONS.VIEW_ALL_CANDIDATES)) {
      return true;
    }
    
    // Conference-level users can access conference candidates
    if (hasPermission(currentUser, PERMISSIONS.VIEW_CONFERENCE_CANDIDATES)) {
      return true; // Simplified - would need conference mapping
    }
    
    // District-level users can only access candidates in their districts
    if (hasPermission(currentUser, PERMISSIONS.VIEW_DISTRICT_CANDIDATES)) {
      // Find the district ID for this candidate's district name
      const candidateDistrict = districts.find(d => d.name === candidateData.district);
      if (candidateDistrict) {
        return canAccessDistrict(currentUser, candidateDistrict.id);
      }
      // If no district assigned to candidate, allow access for now
      return true;
    }
    
    return false;
  };

  // Check if current user can edit candidates
  const canUserEditCandidate = () => {
    if (!currentUser) return false;
    
    // Check for management permissions (not just view permissions)
    return hasPermission(currentUser, PERMISSIONS.MANAGE_ALL_CANDIDATES) ||
           hasPermission(currentUser, PERMISSIONS.MANAGE_CONFERENCE_CANDIDATES) ||
           hasPermission(currentUser, PERMISSIONS.MANAGE_DISTRICT_CANDIDATES);
  };

  const fetchStatusConfig = async () => {
    try {
      const configDoc = await getDoc(doc(db, 'systemConfig', 'statusOptions'));
      if (configDoc.exists()) {
        setStatusConfig(configDoc.data());
      }
    } catch (error) {
      console.error('Error fetching status config:', error);
    }
  };

  const fetchDistricts = async () => {
    try {
      const districtsRef = collection(db, 'districts');
      const snapshot = await getDocs(districtsRef);
      
      const districtsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDistricts(districtsList);
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const handleSave = async () => {
    try {
      const updateData = { ...editData };
      // Ensure districtId is always set (backfills missing field on existing candidates)
      if (!updateData.districtId && updateData.district) {
        const matchedDistrict = districts.find(d => d.name === updateData.district);
        if (matchedDistrict) updateData.districtId = matchedDistrict.id;
      }
      // Capture changed fields for the audit log diff
      const changedFields = Object.keys(updateData).reduce((acc, key) => {
        if (updateData[key] !== candidate[key]) acc[key] = candidate[key];
        return acc;
      }, {});

      await updateCandidate(id, updateData, currentUser, 'profile_update', changedFields);
      setCandidate({ ...candidate, ...updateData });
      setEditing(false);
      toast.success('Profile saved successfully.');
    } catch (error) {
      console.error('Error updating candidate:', error);
      toast.error('Error updating candidate. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDelete = async () => {
    const fullName = `${candidate.firstName} ${candidate.middleName ? candidate.middleName + ' ' : ''}${candidate.lastName}`;
    const confirmed = await confirm({
      title: 'Delete Candidate',
      message: `Are you sure you want to delete ${fullName}? This action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (confirmed) {
      try {
        await softDelete('candidates', id, currentUser);
        navigate('/candidates');
      } catch (error) {
        console.error('Error deleting candidate:', error);
        toast.error('Error deleting candidate. Please try again.');
      }
    }
  };

  // Generate available years for submission (e.g., 2020-current year)
  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    const startYear = 2020; // You can adjust this based on when submissions started
    const years = [];
    for (let year = currentYear; year >= startYear; year--) {
      years.push(year);
    }
    return years;
  };

  if (loading) {
    return (
      <div className="candidate-profile-container">
        <SkeletonProfile />
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="candidate-profile-container">
        <div className="error-message">Candidate not found</div>
      </div>
    );
  }

  const fullName = `${candidate.firstName}${candidate.middleName ? ' ' + candidate.middleName : ''} ${candidate.lastName}`;

  return (
    <div className="candidate-profile-container">
      <Breadcrumb items={[
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Candidates', to: '/candidates' },
        { label: fullName },
      ]} />
      <div className="profile-header">
        <div className="profile-info">
          <h1>{candidate.firstName} {candidate.middleName ? `${candidate.middleName} ` : ''}{candidate.lastName}</h1>
          <div className="profile-badges">
            <span 
              className={`badge candidate-type ${candidate.conferenceStatus || candidate.candidateType}`}
              style={getConferenceStatusStyle(candidate.conferenceStatus || candidate.candidateType, statusConfig)}
            >
              {getConferenceStatusLabel(candidate.conferenceStatus || candidate.candidateType, statusConfig)}
            </span>
            <span 
              className={`badge candidacy-stage ${candidate.processStage || candidate.candidacyStage}`}
              style={getProcessStageStyle(candidate.processStage || candidate.candidacyStage, statusConfig)}
            >
              {getProcessStageLabel(candidate.processStage || candidate.candidacyStage, statusConfig)}
            </span>
          </div>
        </div>
        <div className="profile-actions">
          <button 
            onClick={() => navigate('/candidates')}
            className="back-btn"
          >
            Back to Candidates
          </button>
          {editing ? (
            <>
              <button onClick={handleSave} className="save-btn">Save</button>
              <button onClick={() => setEditing(false)} className="cancel-btn">Cancel</button>
            </>
          ) : (
            <>
              {canUserEditCandidate() && (
                <button onClick={() => setEditing(true)} className="edit-btn">Edit</button>
              )}
              {canUserEditCandidate() && (
                <button onClick={handleDelete} className="delete-btn">Delete</button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="profile-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'course-of-study' ? 'active' : ''}`}
          onClick={() => setActiveTab('course-of-study')}
        >
          Course of Study
        </button>
        <button 
          className={`tab ${activeTab === 'transcripts' ? 'active' : ''}`}
          onClick={() => setActiveTab('transcripts')}
        >
          Transcripts
        </button>
        <button 
          className={`tab ${activeTab === 'action-reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('action-reports')}
        >
          Action Reports
        </button>
        <button 
          className={`tab ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          Documents
        </button>
        <button 
          className={`tab ${activeTab === 'annual-submission' ? 'active' : ''}`}
          onClick={() => setActiveTab('annual-submission')}
        >
          Annual Submission Portal
        </button>
      </div>

      <div className="profile-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="info-sections">
              <div className="info-section">
                <h3>Personal Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>First Name:</label>
                    {editing ? (
                      <input
                        type="text"
                        name="firstName"
                        value={editData.firstName || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.firstName}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Middle Name:</label>
                    {editing ? (
                      <input
                        type="text"
                        name="middleName"
                        value={editData.middleName || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.middleName || 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Last Name:</label>
                    {editing ? (
                      <input
                        type="text"
                        name="lastName"
                        value={editData.lastName || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.lastName}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Email:</label>
                    {editing ? (
                      <input
                        type="email"
                        name="email"
                        value={editData.email || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.email}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Phone:</label>
                    {editing ? (
                      <input
                        type="tel"
                        name="phone"
                        value={editData.phone || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.phone || 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Date of Birth:</label>
                    {editing ? (
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={editData.dateOfBirth || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.dateOfBirth || 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Gender:</label>
                    {editing ? (
                      <select
                        name="gender"
                        value={editData.gender || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="non-binary">Non-Binary</option>
                        <option value="prefer-not-to-say">Prefer Not to Say</option>
                      </select>
                    ) : (
                      <span>{candidate.gender ? candidate.gender.charAt(0).toUpperCase() + candidate.gender.slice(1).replace('-', ' ') : 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Ethnicity:</label>
                    {editing ? (
                      <select
                        name="ethnicity"
                        value={editData.ethnicity || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Ethnicity</option>
                        <option value="african-american">African American</option>
                        <option value="asian">Asian</option>
                        <option value="black">Black</option>
                        <option value="hispanic-latino">Hispanic/Latino</option>
                        <option value="native-american">Native American</option>
                        <option value="pacific-islander">Pacific Islander</option>
                        <option value="multiracial">Multiracial</option>
                        <option value="other">Other</option>
                        <option value="white">White</option>

                        <option value="prefer-not-to-say">Prefer Not to Say</option>
                      </select>
                    ) : (
                      <span>{candidate.ethnicity ? candidate.ethnicity.charAt(0).toUpperCase() + candidate.ethnicity.slice(1).replace('-', ' ') : 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Primary Language:</label>
                    {editing ? (
                      <select
                        name="language"
                        value={editData.language || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Language</option>
                        <option value="english">English</option>
                        <option value="spanish">Spanish</option>
                        <option value="french">French</option>
                        <option value="korean">Korean</option>
                        <option value="chinese">Chinese</option>
                        <option value="vietnamese">Vietnamese</option>
                        <option value="tagalog">Tagalog</option>
                        <option value="portuguese">Portuguese</option>
                        <option value="other">Other</option>
                      </select>
                    ) : (
                      <span>{candidate.language ? candidate.language.charAt(0).toUpperCase() + candidate.language.slice(1) : 'Not provided'}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h3>Address</h3>
                <div className="info-grid">
                  <div className="info-item full-width">
                    <label>Street Address:</label>
                    {editing ? (
                      <input
                        type="text"
                        name="address"
                        value={editData.address || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.address || 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>City:</label>
                    {editing ? (
                      <input
                        type="text"
                        name="city"
                        value={editData.city || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.city || 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>State:</label>
                    {editing ? (
                      <input
                        type="text"
                        name="state"
                        value={editData.state || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.state || 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>ZIP Code:</label>
                    {editing ? (
                      <input
                        type="text"
                        name="zipCode"
                        value={editData.zipCode || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.zipCode || 'Not provided'}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h3>Ministry Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Conference Status:</label>
                    {editing ? (
                      <select
                        name="conferenceStatus"
                        value={editData.conferenceStatus || editData.candidateType || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Conference Status</option>
                        {statusConfig && statusConfig.conferenceStatuses ? 
                          statusConfig.conferenceStatuses
                            .filter(status => status.enabled)
                            .map(status => (
                              <option key={status.id} value={status.id}>
                                {status.label}
                              </option>
                            )) :
                          <>
                            <option value="lay">Lay</option>
                            <option value="clm">Certified Lay Ministry</option>
                            <option value="cmc">Certified Ministry Candidate</option>
                            <option value="supply-pastor">Supply Pastor</option>
                            <option value="local-pastor">Local Pastor</option>
                            <option value="other-fellowship">Other Fellowship</option>
                            <option value="other">Other</option>
                          </>
                        }
                      </select>
                    ) : (
                      <span>{getConferenceStatusLabel(candidate.conferenceStatus || candidate.candidateType, statusConfig)}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Process Stage:</label>
                    {editing ? (
                      <select
                        name="processStage"
                        value={editData.processStage || editData.candidacyStage || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Process Stage</option>
                        {statusConfig && statusConfig.processStages ? 
                          statusConfig.processStages
                            .filter(stage => stage.enabled)
                            .map(stage => (
                              <option key={stage.id} value={stage.id}>
                                {stage.label}
                              </option>
                            )) :
                          <>
                            <option value="inquiring">Inquiring</option>
                            <option value="certified">Certified</option>
                            <option value="full-time">Full-Time</option>
                            <option value="part-time">Part-Time</option>
                            <option value="completed">Completed</option>
                            <option value="discontinued">Discontinued</option>
                            <option value="archived">Archived</option>
                          </>
                        }
                      </select>
                    ) : (
                      <span>{getProcessStageLabel(candidate.processStage || candidate.candidacyStage, statusConfig)}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>District:</label>
                    {editing ? (
                      <select
                        name="district"
                        value={editData.district || ''}
                        onChange={(e) => {
                          const selectedName = e.target.value;
                          const selectedDistrict = districts.find(d => d.name === selectedName);
                          setEditData(prev => ({
                            ...prev,
                            district: selectedName,
                            districtId: selectedDistrict?.id || '',
                          }));
                        }}
                      >
                        <option value="">Select District</option>
                        {districts.map(district => (
                          <option key={district.id} value={district.name}>
                            {district.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span>{candidate.district || 'Not assigned'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Home Church:</label>
                    {editing ? (
                      <input
                        type="text"
                        name="homeChurch"
                        value={editData.homeChurch || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.homeChurch || 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Appointment or Charge Conference:</label>
                    {editing ? (
                      <input
                        type="text"
                        name="currentAppointment"
                        value={editData.currentAppointment || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.currentAppointment || 'Not assigned'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>First Year License Awarded:</label>
                    {editing ? (
                      <input
                        type="number"
                        name="firstYearLicense"
                        value={editData.firstYearLicense || ''}
                        onChange={handleInputChange}
                        placeholder="YYYY"
                        min="1900"
                        max={new Date().getFullYear()}
                      />
                    ) : (
                      <span>{candidate.firstYearLicense || 'Not recorded'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Mentor:</label>
                    {editing ? (
                      <input
                        type="text"
                        name="mentor"
                        value={editData.mentor || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.mentor || 'Not assigned'}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h3>Emergency Contact</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Contact Name:</label>
                    {editing ? (
                      <input
                        type="text"
                        name="emergencyContact"
                        value={editData.emergencyContact || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.emergencyContact || 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item">
                    <label>Contact Phone:</label>
                    {editing ? (
                      <input
                        type="tel"
                        name="emergencyPhone"
                        value={editData.emergencyPhone || ''}
                        onChange={handleInputChange}
                      />
                    ) : (
                      <span>{candidate.emergencyPhone || 'Not provided'}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="info-section">
                <h3>Additional Information</h3>
                <div className="info-grid">
                  <div className="info-item full-width">
                    <label>Educational Background:</label>
                    {editing ? (
                      <textarea
                        name="education"
                        value={editData.education || ''}
                        onChange={handleInputChange}
                        rows="3"
                      />
                    ) : (
                      <span>{candidate.education || 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item full-width">
                    <label>Previous Ministry Experience:</label>
                    {editing ? (
                      <textarea
                        name="previousExperience"
                        value={editData.previousExperience || ''}
                        onChange={handleInputChange}
                        rows="3"
                      />
                    ) : (
                      <span>{candidate.previousExperience || 'Not provided'}</span>
                    )}
                  </div>
                  <div className="info-item full-width">
                    <label>Notes:</label>
                    {editing ? (
                      <textarea
                        name="notes"
                        value={editData.notes || ''}
                        onChange={handleInputChange}
                        rows="3"
                      />
                    ) : (
                      <span>{candidate.notes || 'No notes'}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'course-of-study' && (
          <CourseOfStudyTracker candidateId={id} candidate={candidate} />
        )}

        {activeTab === 'transcripts' && (
          <TranscriptManager candidateId={id} candidate={candidate} />
        )}

        {activeTab === 'action-reports' && (
          <ActionReportsList candidateId={id} candidate={candidate} />
        )}

        {activeTab === 'documents' && (
          <DocumentManager candidateId={id} />
        )}

        {activeTab === 'annual-submission' && (
          <div className="annual-submission-tab">
            <div className="year-selector-container">
              <label htmlFor="submission-year-select">Viewing Submission Year:</label>
              <select
                id="submission-year-select"
                value={selectedSubmissionYear}
                onChange={(e) => setSelectedSubmissionYear(parseInt(e.target.value))}
                className="year-selector"
              >
                {getAvailableYears().map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            <CandidateSubmissionPortal 
              candidateId={id} 
              submissionYear={selectedSubmissionYear} 
              readOnly={false} 
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateProfile;
